import { useState } from 'preact/hooks'
import { apiClient, InitData, navigerUd, returMaal } from '../core'
import { t, formatMoney, formatMoneyParts } from '../core/i18n'
import { PaymentMethodPicker, hasPaymentChoice, type InlinePaymentMethod } from './PaymentMethodPicker'
import { CheckoutLegal, checkoutConsent, initialCheckoutLegalState, type CheckoutLegalState } from './CheckoutLegal'

interface Props {
  initData: InitData
  /** Handoff-URL til studiets egen gavekort-side — fallback når inline-køb ikke kan gennemføres. */
  giftHref: string
  onClose: () => void
}

interface GiftCardResponse {
  success?: boolean
  requiresPayment?: boolean
  redirectUrl?: string
  reference?: string
  giftCardCode?: string
}

/**
 * GiftCardModal — inline gavekort-KØB for embed-widgeten (Feature 3).
 *
 * Anonymt (som den native /gavekort — køberen opgiver navn+email, intet login).
 * Kalder den DELTE kerne via `/api/widget/gift-cards/checkout` → Stripe/MobilePay på
 * tenantens egne nøgler → redirect til betaling, hjem via /widget-retur. Enhver
 * ok:false (beløb uden for grænser, metode ej konfigureret) viser fejlen + beholder
 * handoff-linket, så kunden altid kan fuldføre på studiets egen side.
 */
export function GiftCardModal({ initData, giftHref, onClose }: Props) {
  const s = t(initData.locale)
  const [amount, setAmount] = useState('500')
  const [yourName, setYourName] = useState('')
  const [yourEmail, setYourEmail] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // MobilePay eller kort, når studiet har begge (MobilePay forvalgt = serverens standard).
  const [payMethod, setPayMethod] = useState<InlinePaymentMethod>('mobilepay')
  const showPayPicker = hasPaymentChoice(initData)
  // Ordreoversigt + samtykke før købet (jura-hs p44).
  const [legal, setLegal] = useState<CheckoutLegalState>(initialCheckoutLegalState)

  const marker = formatMoneyParts(0, initData.locale, initData.currency).symbol
  const amountNum = Math.round(Number(amount) || 0)
  const kanKoebe = amountNum >= 100 && amountNum <= 5000 && yourName.trim() !== '' && yourEmail.trim() !== '' && !submitting && legal.termsAccepted

  async function handleBuy() {
    if (submitting) return
    setSubmitting(true)
    setError(null)

    // Anonymt køb → requireAuth=false (default). Serveren validerer autoritativt.
    const result = await apiClient.post<GiftCardResponse>('/api/widget/gift-cards/checkout', {
      amount: amountNum,
      purchaserName: yourName.trim(),
      purchaserEmail: yourEmail.trim(),
      ...(recipientEmail.trim() ? { recipientEmail: recipientEmail.trim() } : {}),
      ...(message.trim() ? { personalMessage: message.trim() } : {}),
      // Kun et eksplicit valg når der var noget at vælge; ellers vælger serveren.
      ...(showPayPicker ? { paymentMethod: payMethod } : {}),
      ...(returMaal() ? { returnUrl: returMaal() } : {}),
      legalConsent: checkoutConsent(legal, initData.locale),
    })

    setSubmitting(false)

    if (result.data?.redirectUrl) {
      navigerUd(result.data.redirectUrl)
      return
    }
    // ok:false → vis fejlen; handoff-linket nedenfor er altid tilgængeligt.
    setError(result.error || s.loadError)
  }

  const stop = (e: Event) => e.stopPropagation()

  return (
    <div class="hsw-gcm-backdrop" onClick={onClose}>
      <style>{modalStyles}</style>
      <div class="hsw-gcm-dialog" onClick={stop} role="dialog" aria-modal="true" aria-labelledby="hsw-gcm-title">
        <button class="hsw-gcm-close" onClick={onClose} aria-label={s.close}>×</button>
        <h3 id="hsw-gcm-title" class="hsw-gcm-title">{s.gcBuyTitle}</h3>

        <label class="hsw-gcm-label">
          {s.gcBuyAmount} ({marker})
          <input class="hsw-gcm-input" type="number" min={100} max={5000} step={50}
            value={amount} onInput={(e) => setAmount((e.target as HTMLInputElement).value)} />
        </label>
        <label class="hsw-gcm-label">
          {s.gcBuyYourName}
          <input class="hsw-gcm-input" type="text" value={yourName} autocomplete="name"
            onInput={(e) => setYourName((e.target as HTMLInputElement).value)} />
        </label>
        <label class="hsw-gcm-label">
          {s.gcBuyYourEmail}
          <input class="hsw-gcm-input" type="email" value={yourEmail} autocomplete="email"
            onInput={(e) => setYourEmail((e.target as HTMLInputElement).value)} />
        </label>
        <label class="hsw-gcm-label">
          {s.gcBuyRecipientEmail}
          <input class="hsw-gcm-input" type="email" value={recipientEmail}
            onInput={(e) => setRecipientEmail((e.target as HTMLInputElement).value)} />
        </label>
        <label class="hsw-gcm-label">
          {s.gcBuyMessage}
          <textarea class="hsw-gcm-input" rows={2} value={message}
            onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)} />
        </label>

        {showPayPicker && (
          <PaymentMethodPicker
            initData={initData}
            value={payMethod}
            onChange={setPayMethod}
            disabled={submitting}
          />
        )}
        <CheckoutLegal
          s={s.checkoutLegal}
          initData={initData}
          lines={[{ label: s.gcBuyTitle }]}
          total={amountNum > 0 ? formatMoney(amountNum, initData.locale, initData.currency) : undefined}
          kind="one_time"
          state={legal}
          onChange={setLegal}
          disabled={submitting}
        />
        {error && <div class="hsw-gcm-error" role="alert">{error}</div>}

        <button class="hsw-gcm-primary" onClick={handleBuy} disabled={!kanKoebe}>
          {submitting ? s.gcBuySubmitting : s.checkoutLegal.payGeneric}
        </button>
        {/* Handoff altid tilgængelig: kan inline-købet ikke gennemføres, fuldfører
            kunden på studiets egen gavekort-side. */}
        <a class="hsw-gcm-handoff" href={giftHref} target="_blank" rel="noopener" onClick={onClose}>
          {s.continueToPurchase}
        </a>
        <button class="hsw-gcm-secondary" onClick={onClose} disabled={submitting}>{s.cancel}</button>
      </div>
    </div>
  )
}

const modalStyles = `
  .hsw-gcm-backdrop {
    position: fixed; inset: 0; background: rgba(0,0,0,0.5);
    display: flex; align-items: center; justify-content: center;
    z-index: 100000; padding: 1rem; animation: hsw-gcm-fade .15s ease-out;
  }
  @keyframes hsw-gcm-fade { from { opacity: 0; } to { opacity: 1; } }
  .hsw-gcm-dialog {
    position: relative; background: var(--yoga-card-bg, #ffffff);
    border-radius: var(--yoga-radius, 8px); padding: 2rem 1.75rem 1.75rem;
    width: 100%; max-width: 440px; max-height: 90vh; overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2); color: var(--yoga-text, #0a0a0a); box-sizing: border-box;
  }
  .hsw-gcm-close {
    position: absolute; top: 0.5rem; right: 0.75rem; background: transparent; border: 0;
    font-size: 1.75rem; line-height: 1; color: var(--yoga-text-muted, #737373); cursor: pointer; padding: 0.25rem 0.6rem;
  }
  .hsw-gcm-close:hover { color: var(--yoga-text, #0a0a0a); }
  .hsw-gcm-title { font-size: 1.4rem; font-weight: 700; margin: 0 0 1rem; letter-spacing: -0.01em; }
  .hsw-gcm-label {
    display: block; font-size: 0.85rem; color: var(--yoga-text-muted, #525252);
    margin-bottom: 0.75rem;
  }
  .hsw-gcm-input {
    font: inherit; width: 100%; box-sizing: border-box; margin-top: 0.3rem;
    padding: 0.6rem 0.75rem; border: 1px solid var(--yoga-border, #d4d4d4);
    border-radius: var(--yoga-radius, 0); background: transparent; color: inherit;
  }
  .hsw-gcm-error {
    background: #fef2f2; border: 1px solid #fecaca; color: #b91c1c;
    padding: 0.7rem 0.85rem; border-radius: var(--yoga-radius, 6px); font-size: 0.85rem; margin-bottom: 0.75rem;
  }
  .hsw-gcm-primary {
    display: block; width: 100%; box-sizing: border-box; margin-top: 0.5rem;
    background: var(--yoga-primary, #FF6B35); color: #fff; border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 6px); padding: 0.85rem 1.25rem; font-size: 0.95rem; font-weight: 600;
    cursor: pointer; transition: background .2s, opacity .2s; font-family: inherit;
  }
  .hsw-gcm-primary:hover:not(:disabled) { background: var(--yoga-primary-900, #1A1A2E); border-color: var(--yoga-primary-900, #1A1A2E); }
  .hsw-gcm-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .hsw-gcm-handoff {
    display: block; text-align: center; margin-top: 0.75rem; font-size: 0.85rem; font-weight: 500;
    color: var(--yoga-primary, #FF6B35); text-decoration: underline; cursor: pointer;
  }
  .hsw-gcm-secondary {
    display: block; width: 100%; box-sizing: border-box; margin-top: 0.75rem; background: transparent;
    color: var(--yoga-text, #0a0a0a); border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 6px); padding: 0.7rem 1.25rem; font-size: 0.9rem; cursor: pointer; font-family: inherit;
  }
  .hsw-gcm-secondary:hover:not(:disabled) { background: rgba(0,0,0,0.04); }
  .hsw-gcm-secondary:disabled { opacity: 0.5; cursor: not-allowed; }
`
